/* eslint-disable react/display-name */
import { DropdownOptionItem } from './DropdownOption/DropdownOptionProps';
import { DropdownOwnProps, DropdownProps } from './DropdownProps';
import DropdownMenu from './DropdownMenu';
import React, { forwardRef, useMemo, useRef, useState, useEffect } from 'react';
import { useStyleConfig } from '@chakra-ui/react';
import {
  StyledDropdown,
  StyledDropdownControlWrap,
  StyledDropdownToggle,
  StyledDropdownToggleText,
  StyledLabel,
} from './StyledDropdown';
import BEIcon from '@molecules/BEIcon';
import { usePopover } from '@molecules/BEOffScreen';
import FormGroup from '../FormGroup';
import { Controller, FieldError } from 'react-hook-form';

const DEFAULT_RENDER_VALUE = (
  selectedOptions: DropdownOptionItem | DropdownOptionItem[],
  placeholder = ''
) => {
  if (Array.isArray(selectedOptions) && selectedOptions.length) {
    return selectedOptions.map((o) => o.text).join(', ');
  }

  if (!Array.isArray(selectedOptions) && selectedOptions) {
    return selectedOptions.text;
  }

  return placeholder;
};

const Dropdown = forwardRef<HTMLInputElement, DropdownOwnProps>(
  (
    {
      className,
      bordered = true,
      disabled,
      label,
      labelHidden = false,
      labelPosition = 'default',
      multiple = false,
      name,
      icon,
      iconWeight,
      onChange: handleChange,
      options,
      placeholder,
      renderValue = DEFAULT_RENDER_VALUE,
      showCheck = false,
      value,
      error = false,
    },
    ref
  ) => {
    const styles = useStyleConfig('Dropdown', {
      variant:
        labelPosition === 'top'
          ? 'labelTop'
          : labelPosition === 'left'
          ? 'labelLeft'
          : 'labelFloat',
    });
    const [_value, setValue] = useState(value);

    useEffect(() => {
      setValue(value);
    }, [value]);

    const wrapperRef = useRef<HTMLDivElement>(null);
    const [toggleRef, setToggleRef] = useState<any>();

    const selectedOptions = useMemo(() => {
      return Array.isArray(_value)
        ? _value.map((v) => options?.find((o) => o.value === v))
        : options?.find((o) => o.value === _value);
    }, [_value, options]);

    const popover = usePopover(DropdownMenu, {
      id: `popover-${name}`,
      referenceElement: toggleRef,
      width: toggleRef?.offsetWidth,
    });
    const isOpen = popover.isOpened();

    const onSelect = (v) => {
      if (!multiple) {
        popover.close();
      }
      handleChange?.(v);
      setValue(v);
    };

    const handleToggleClick = () => {
      if (isOpen) {
        popover.close();
      } else {
        popover.open({
          inputRef: ref,
          multiple,
          name,
          options,
          showCheck,
          value: _value,
          onSelect,
        });
      }
    };

    return (
      <StyledDropdown
        className={className}
        hasIcon={Boolean(icon)}
        labelPosition={labelPosition}
        ref={wrapperRef}
        __css={styles}
      >
        <StyledLabel
          className={
            isOpen || (Array.isArray(_value) ? _value.length > 0 : _value)
              ? 'Active'
              : ''
          }
          hasIcon={Boolean(icon)}
          labelPosition={labelPosition}
          hidden={labelHidden}
          htmlFor={'toggle-' + name}
          id={name}
          error={error}
        >
          {label}
        </StyledLabel>
        <StyledDropdownControlWrap labelPosition={labelPosition}>
          <StyledDropdownToggle
            id={'toggle-' + name}
            hasIcon={Boolean(icon)}
            bordered={bordered}
            disabled={disabled}
            className={disabled ? 'Disabled' : ''}
            onClick={handleToggleClick}
            ref={setToggleRef}
            h={labelPosition === 'float' ? '54px' : '48px'}
            valueSet={
              (Array.isArray(value) && value.length) || (value && value !== '')
            }
            error={error}
          >
            {icon && <BEIcon name={icon} size="sm" weight={iconWeight} />}
            <StyledDropdownToggleText labelPosition={labelPosition}>
              {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
              {/* @ts-ignore */}
              {renderValue(
                selectedOptions,
                labelPosition !== 'float' && placeholder
              )}
            </StyledDropdownToggleText>
            <BEIcon name={isOpen ? 'angle-up' : 'angle-down'} size="sm" />
          </StyledDropdownToggle>
        </StyledDropdownControlWrap>
      </StyledDropdown>
    );
  }
);

const FormDropdown = ({
  label,
  name,
  rules,
  control,
  formState,
  onChange,
  ...dropdownProps
}: DropdownProps) => {
  const fieldRef = useRef<HTMLInputElement | null>();

  return (
    <FormGroup
      error={formState?.errors?.[name] as FieldError}
      label={label}
      name={name}
      required={!!rules?.required}
    >
      <Controller
        control={control}
        defaultValue={null}
        name={name}
        render={({
          field: { onChange: onChangeSelect, ref, value, ...props },
        }) => (
          <Dropdown
            ref={(r) => {
              fieldRef.current = r;
              ref(r);
            }}
            label={label}
            {...dropdownProps}
            value={value}
            onChange={(v: any) => {
              onChangeSelect(v);
              onChange?.(v);
            }}
            {...props}
          />
        )}
        rules={rules}
      />
    </FormGroup>
  );
};

export default FormDropdown;
